/**
 * A chosen plan's saved record, brought up to date by a run that found the same plan again (25 Oct 2026).
 *
 * Home and each module's plan page read the figures saved when the plan was chosen; the plan's card reads
 * the latest run. When the rules or the day moved in between, the two disagreed for one plan ($3,286,281
 * on Home, $3,285,977 on the card). The record keeps who it is (id, when it was chosen, anything else on
 * it) and takes the run's figures. Returns null when nothing would change, so callers write nothing.
 */
type PlanRecord = Record<string, unknown> & { id?: unknown; type?: unknown; chosen?: unknown; source?: unknown; outputs?: Record<string, unknown> };

const FIGURES = ['legacyEstimate', 'cashFlow', 'agePension', 'agedCareCost', 'annualContribution'] as const;

const sameFigures = (a: Record<string, unknown> | undefined, b: Record<string, unknown> | undefined): boolean =>
    FIGURES.every((k) => Math.round(Number(a?.[k]) || 0) === Math.round(Number(b?.[k]) || 0));

export function refreshChosenRecord<T extends PlanRecord>(
    records: ReadonlyArray<T>,
    fresh: T,
    isSamePlan: (saved: T, fresh: T) => boolean = sameAgedCarePlan as never,
    now: Date = new Date(),
): T[] | null {
    const at = records.findIndex((r) => r.type === fresh.type && r.chosen === true && r.source === fresh.source && isSamePlan(r, fresh));
    if (at < 0) return null;
    const saved = records[at]!;
    if (sameFigures(saved.outputs, fresh.outputs) && !saved.outOfDate) return null;
    const next = [...records];
    const { outOfDate: _found, ...kept } = { ...saved, ...fresh } as T & { outOfDate?: unknown };
    next[at] = { ...kept, id: saved.id, createdAt: saved.createdAt, chosen: true, retestedAt: now.toISOString() } as unknown as T;
    return next;
}

/*
 * A CHOSEN PLAN THE NEXT RUN NO LONGER HAS (25 Oct 2026). A Support at home plan chosen with both at home was not
 * among the plans once John's aged care plan was carried in, so nothing refreshed it and Home kept showing its old
 * figure as if current. The record stays chosen (choosing is the client's act), and says why it is out of date, so
 * every screen can say so instead of quoting its figures as today's.
 */
export interface PlanOutOfDate { reason: string; at: string }

export function outOfDateOf(record: unknown): PlanOutOfDate | null {
    const o = (record as { outOfDate?: Partial<PlanOutOfDate> } | null | undefined)?.outOfDate;
    return o && typeof o.reason === 'string' && o.reason ? { reason: o.reason, at: String(o.at || '') } : null;
}

/** The chosen record of `type` marked out of date for `reason`; null when it is already, or there is none. */
export function markChosenOutOfDate<T extends PlanRecord>(records: ReadonlyArray<T>, type: string, reason: string, now: Date = new Date()): T[] | null {
    const at = records.findIndex((r) => r.type === type && r.chosen === true);
    if (at < 0 || outOfDateOf(records[at])?.reason === reason) return null;
    const next = [...records];
    next[at] = { ...records[at]!, outOfDate: { reason, at: now.toISOString() } };
    return next;
}

/** Said wherever an out-of-date chosen plan appears; its figures are the ones it was chosen on, not today's. */
export const NOT_FOUND_ON_RETEST = 'It was not among the plans when they were tested again, so its figures are out of date — open it to choose again.';

/** The same aged care engine plan: the same row (name), for the same room, paying the same deposit. */
export function sameAgedCarePlan(saved: PlanRecord, fresh: PlanRecord): boolean {
    const a = (saved.strategy || {}) as { name?: unknown; radRoomPrice?: unknown; radPaidNet?: unknown };
    const b = (fresh.strategy || {}) as { name?: unknown; radRoomPrice?: unknown; radPaidNet?: unknown };
    return a.name != null && String(a.name) === String(b.name)
        && Math.abs((Number(a.radRoomPrice) || 0) - (Number(b.radRoomPrice) || 0)) < 1
        && Math.abs((Number(a.radPaidNet) || 0) - (Number(b.radPaidNet) || 0)) < 1;
}

/*
 * THE VILLAGE, DOWNSIZING, THE GRANNY FLAT AND CASHFLOW (owner, 25 Oct 2026: the same as aged care and Support at
 * home). A chosen plan records the client's figures it was priced on (`inputsFingerprint`); when they have changed,
 * Home and the plan page say the plan is out of date instead of quoting its old figures as today's. A re-test that
 * finds the plan again brings its figures up to date (and clears the mark); one on the same contract or home that
 * no longer has it marks it out of date. Saves from before carry no fingerprint and are never marked by it.
 */

/** Said where the client's own figures changed after the plan was chosen. */
export const PROFILE_CHANGED = 'Chosen before the figures on the file were changed, so its figures are out of date — open it to choose again.';

type Bag = Record<string, unknown>;
const amountOfItem = (item: unknown): number => {
    const o = (item || {}) as Bag;
    return Math.round(Number(o.value ?? o.balance ?? o.amount ?? o.outstanding ?? 0) || 0);
};
const listAmounts = (group: unknown): string =>
    Object.keys((group || {}) as Bag).sort().map((k) => {
        const v = (group as Bag)[k];
        return `${k}:${Array.isArray(v) ? v.map(amountOfItem).join(',') : ''}`;
    }).join('|');
const numericLeaves = (o: unknown, path = ''): string[] => {
    if (o == null) return [];
    if (typeof o === 'number') return Number.isFinite(o) ? [`${path}=${Math.round(o)}`] : [];
    if (typeof o === 'boolean') return [`${path}=${o}`];
    if (Array.isArray(o)) return o.flatMap((v, i) => numericLeaves(v, `${path}[${i}]`));
    if (typeof o === 'object') return Object.keys(o as Bag).sort().filter((k) => !/^id$|At$/.test(k)).flatMap((k) => numericLeaves((o as Bag)[k], `${path}.${k}`));
    return [];
};

/**
 * The client's own figures a plan is priced on, as a short fingerprint: who, their ages, the home, what they own
 * and owe (each item's amount), their income and living costs. Only amounts and dates of birth, never ids or
 * labels, so renaming an account does not mark a plan out of date.
 */
export function profileFingerprint(factFind: unknown): string {
    const f = (factFind || {}) as Bag;
    const person = (p: unknown) => String(((p || {}) as Bag).dob || '');
    const text = [
        String(f.adviceFor || ''),
        person(f.client),
        f.adviceFor === 'couple' ? person(f.partner) : '',
        String(Math.round(Number(((f.home || {}) as Bag).value) || 0)),
        listAmounts(f.assets),
        listAmounts(f.liabilities),
        numericLeaves(f.income).join(';'),
        String(Math.round(Number(((f.expenses || {}) as Bag).livingExpenses) || 0)),
    ].join('#');
    let h = 5381;
    for (let i = 0; i < text.length; i++) h = ((h * 33) ^ text.charCodeAt(i)) >>> 0;
    return h.toString(36);
}

/** Why a chosen village, downsizing, granny flat or cashflow plan is out of date, or null. */
export function chosenPlanOutOfDate(record: unknown, factFind: unknown): string | null {
    if (!record) return null;
    const marked = outOfDateOf(record);
    if (marked) return marked.reason;
    const saved = (record as { inputsFingerprint?: unknown }).inputsFingerprint;
    return typeof saved === 'string' && saved && saved !== profileFingerprint(factFind) ? PROFILE_CHANGED : null;
}

export interface RetestFigures { legacy: number; pension: number; cashflow: number }

/**
 * The chosen record `id`, brought up to date by a re-test that found it again: its card's figures (and the
 * benchmark's, where given), its outputs, the fingerprint it is now priced on, and no out-of-date mark. Null when
 * nothing would change, so callers write nothing (and no render loop starts).
 */
export function retestChosenRecord<T extends Bag>(
    records: ReadonlyArray<T>,
    id: unknown,
    plan: RetestFigures,
    baseline: RetestFigures | null,
    fingerprint: string,
    now: Date = new Date(),
): T[] | null {
    const at = records.findIndex((r) => r.id === id);
    if (at < 0) return null;
    const saved = records[at]!;
    const inputs = (saved.inputs || {}) as Bag;
    const card = (inputs.card || null) as Bag | null;
    const outputs = (saved.outputs || {}) as Bag;
    const r = (n: number) => Math.round(Number(n) || 0);
    const cardPlan = (card?.plan || {}) as Bag;
    const same = r(Number(outputs.legacyEstimate)) === r(plan.legacy) && r(Number(outputs.agePension)) === r(plan.pension) && r(Number(outputs.cashFlow)) === r(plan.cashflow)
        && (!card || (r(Number(cardPlan.legacy)) === r(plan.legacy) && r(Number(cardPlan.pension)) === r(plan.pension) && r(Number(cardPlan.cashflow)) === r(plan.cashflow)))
        && !outOfDateOf(saved) && saved.inputsFingerprint === fingerprint;
    if (same) return null;
    const next = [...records];
    const { outOfDate: _gone, ...kept } = saved as T & { outOfDate?: unknown };
    next[at] = {
        ...kept,
        inputsFingerprint: fingerprint,
        retestedAt: now.toISOString(),
        inputs: card ? { ...inputs, card: { ...card, plan: { ...cardPlan, ...plan }, ...(baseline ? { baseline } : {}) } } : inputs,
        outputs: { ...outputs, legacyEstimate: plan.legacy, agePension: plan.pension, cashFlow: plan.cashflow },
    } as unknown as T;
    return next;
}

/** The record `id` marked out of date for `reason`; null when it already is, or is not there. */
export function markRecordOutOfDate<T extends Bag>(records: ReadonlyArray<T>, id: unknown, reason: string, now: Date = new Date()): T[] | null {
    const at = records.findIndex((r) => r.id === id);
    if (at < 0 || outOfDateOf(records[at])?.reason === reason) return null;
    const next = [...records];
    next[at] = { ...records[at]!, outOfDate: { reason, at: now.toISOString() } };
    return next;
}
